import { useEffect, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { ChatMessage } from '../types';
import Citation from './Citation';

interface ChatWindowProps {
  messages: ChatMessage[];
  loading?: boolean;
}

/**
 * Renders chat message list with user questions and system answers.
 * Uses react-markdown for assistant responses (FR-003, FR-004).
 * Summary is injected as the first chat message by ChatPage.
 */
export default function ChatWindow({ messages, loading }: ChatWindowProps) {
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Wait for browser paint before scrolling — fixes race when DOM switches
    // from empty state to chat-window (different DOM tree, layout not ready)
    requestAnimationFrame(() => {
      bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    });
  }, [messages, loading]);

  if (messages.length === 0 && !loading) {
    return (
      <div className="chat-empty">
        <div className="chat-empty-inner">
          <h2>Stig</h2>
          <p>Ask me anything about the DSS milestone — phases, decisions, risks, team availability, and more.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="chat-window">
      {messages.map(msg => (
        <div key={msg.id} className={`message message-${msg.role}`}>
          <div className="message-label">
            {msg.role === 'user' ? 'You' : 'Stig'}
          </div>
          <div className="message-bubble">
            {msg.role === 'assistant' ? (
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{msg.content}</ReactMarkdown>
            ) : (
              msg.content
            )}
            {msg.citations && msg.citations.length > 0 && (
              <div className="citations-bar">
                <span className="citations-label">Sources</span>
                {msg.citations.map((c, i) => (
                  <Citation key={i} citation={c} />
                ))}
              </div>
            )}
            {msg.groundingStatus && msg.groundingStatus !== 'grounded' && (
              <div className={`grounding-warning ${msg.groundingStatus === 'not-found' ? 'grounding-not-found' : 'grounding-partial'}`}>
                {msg.groundingStatus === 'not-found' ? 'Information not found in project data' : 'Partially grounded — some information may be incomplete'}
              </div>
            )}
          </div>
        </div>
      ))}
      {loading && (
        <div className="message message-assistant">
          <div className="message-label">Stig</div>
          <div className="message-bubble">
            <div className="thinking">
              <span className="thinking-dot" />
              <span className="thinking-dot" />
              <span className="thinking-dot" />
            </div>
          </div>
        </div>
      )}
      <div ref={bottomRef} />
    </div>
  );
}
